import { useState, useCallback } from 'react';
import { CountdownTimer } from './CountdownTimer';
import { uploadChekiProof, submitChekiProof } from '../lib/api';

interface Props {
  orderId:         string;
  orderToken:      string;
  deadlineAt:      Date;
  paymentImageUrl: string | null;
  onSuccess:       () => void;
}

export function PaymentStep({ orderId, orderToken, deadlineAt, paymentImageUrl, onSuccess }: Props) {
  const [file, setFile]           = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(() => deadlineAt.getTime() <= Date.now());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState('');

  const handleExpired = useCallback(() => setIsExpired(true), []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  }

  async function handleConfirm() {
    if (!file || isExpired) return;
    setError('');
    setIsLoading(true);
    try {
      const proofUrl = await uploadChekiProof(file);
      await submitChekiProof(orderId, proofUrl, orderToken);
      onSuccess();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg === 'DEADLINE_EXPIRED') {
        setIsExpired(true);
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="mx-auto max-w-md flex flex-col gap-6">
        {/* Timer */}
        <div
          className="flex items-center justify-between rounded-xl border px-4 py-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <span className="text-xs text-white/40">Batas waktu pembayaran</span>
          {isExpired
            ? <span className="text-sm font-bold text-red-400">Waktu Habis</span>
            : <CountdownTimer deadlineAt={deadlineAt} onExpired={handleExpired} />
          }
        </div>

        {/* Payment info */}
        <div>
          <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">Info Pembayaran</p>
          {paymentImageUrl
            ? <img src={paymentImageUrl} alt="Info pembayaran" className="w-full rounded-xl" />
            : (
              <div
                className="rounded-xl border py-8 text-center text-sm text-white/30"
                style={{ borderColor: 'var(--border)' }}
              >
                Info pembayaran belum tersedia. Hubungi admin.
              </div>
            )
          }
        </div>

        {/* Upload */}
        <div>
          <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">Upload Bukti Bayar</p>
          <label
            className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition hover:border-pink-500/40"
            style={{ borderColor: preview ? 'var(--pink)' : 'var(--border)' }}
          >
            {preview
              ? <img src={preview} alt="Preview" className="max-h-48 rounded-lg object-contain" />
              : (
                <>
                  <span className="text-2xl opacity-30">📎</span>
                  <span className="text-sm text-white/30">Pilih gambar bukti transfer</span>
                </>
              )
            }
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={isExpired}
            />
          </label>
        </div>

        {isExpired && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2.5 text-sm text-red-400">
            Waktu habis — pesanan telah dibatalkan secara otomatis.
          </p>
        )}
        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2.5 text-sm text-red-400">{error}</p>
        )}

        <button
          onClick={handleConfirm}
          disabled={!file || isExpired || isLoading}
          className="flex w-full items-center justify-center rounded-xl py-3.5 font-semibold text-white transition disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, var(--pink), var(--pink-dark, #be185d))' }}
        >
          {isLoading
            ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            : 'Konfirmasi Pembayaran →'
          }
        </button>
      </div>
    </div>
  );
}
